import { useState } from 'react';
import { useApp } from '../store';
import { isProjectMode } from '../lifecycle';
import { carryInLessons, isWorkspaceLesson, type Lesson } from '../lessons';
import { LessonCard } from './LessonsView';
import GuidanceRow from './GuidanceRow';

/**
 * LESSONS WORTH CARRYING IN — #490, the reuse half of the 3.19 learning loop.
 *
 * 3.19 captures lessons (board tab + workspace library + after-action report); this
 * is where they come back — at the one moment they change behaviour, the start of
 * the NEXT project. It sits at the top of a young project's Plan, above the setup
 * guide, and surfaces the recommendation-bearing lessons from past projects:
 * those of the SAME type first, then workspace-wide and similar.
 *
 * SUGGEST-ONLY (v1). Nothing is added to the project — it is a read. Seeding a
 * risk from a lesson waits for the Risk register; until that ships there is no
 * control for it here, rather than one that looks live and does nothing.
 *
 * It is the first row of the Plan's "Getting started" group, sharing the setup
 * guide's disclosure grammar (GuidanceRow) so the two read as one family. Collapsed
 * by default — a calm Plan on open (Vinnie, 21 Oct 2026) — and remembered per viewer
 * (localStorage, like ProjectGuide), so v1 needs no migration. Project boards only
 * (a continuous service reads its lessons on a cadence, not at a project start).
 */

const openKey = (projectId: string) => `fd.lessonsAtStart.hidden.${projectId}`;
function readOpen(projectId: string): boolean {
  try {
    // DEFAULT CLOSED: only an explicit 'open' shows it; a first visit (no value) and
    // the legacy '1'/'closed' both keep it folded — a quiet Plan on open.
    return localStorage.getItem(openKey(projectId)) === 'open';
  } catch {
    return false;
  }
}

export default function LessonsAtStart() {
  const projectId = useApp((s) => s.projectId);
  const projects = useApp((s) => s.projects);
  const lessons = useApp((s) => s.lessons);
  const profiles = useApp((s) => s.profiles);
  const setView = useApp((s) => s.setView);
  const project = projects.find((p) => p.id === projectId);

  const [open, setOpen] = useState(() => readOpen(projectId));
  const setOpenPersist = (v: boolean) => {
    setOpen(v);
    try {
      localStorage.setItem(openKey(projectId), v ? 'open' : 'closed');
    } catch {
      /* Private browsing — the panel simply returns to its default (closed) next load. */
    }
  };

  // Project boards only — mirrors the other project-only surfaces.
  if (!project || !isProjectMode(project)) return null;

  const typeOf = (boardId: string) => projects.find((p) => p.id === boardId)?.projectType;
  const currentType = project.projectType?.trim();
  const { sameType, rest, total } = carryInLessons(lessons, {
    currentType,
    currentBoardId: projectId,
    typeOf,
  });

  // Nothing worth carrying in yet (a young workspace) — show no row at all rather
  // than an empty one. The collapsed GuidanceRow is itself the dismissed state, so
  // there is nothing to leave standing for lessons that are not there.
  if (total === 0) return null;

  const CAP = 4;
  const shownSame = sameType.slice(0, CAP);
  const shownRest = rest.slice(0, Math.max(0, CAP - shownSame.length));

  const origin = (l: Lesson) => {
    if (isWorkspaceLesson(l)) {
      return (
        <span className="lorigin ws">
          <span className="pin" />
          Workspace-wide
        </span>
      );
    }
    const label =
      [typeOf(l.boardId!)?.trim(), projects.find((p) => p.id === l.boardId)?.name]
        .filter(Boolean)
        .join(' · ') || 'A project';
    return (
      <span className="lorigin">
        <span className="pin" />
        {label}
      </span>
    );
  };

  const card = (l: Lesson) => (
    <LessonCard
      key={l.id}
      lesson={l}
      profiles={profiles}
      topExtra={origin(l)}
    />
  );

  const sub =
    currentType && sameType.length
      ? 'Recommendations from past projects of this type and across the workspace — read them before you shape the plan.'
      : 'Recommendations captured across the workspace, newest first — read them before you shape the plan.';

  return (
    <GuidanceRow
      title="Lessons worth carrying in"
      peek="What past projects learned"
      count={total}
      open={open}
      onToggle={() => setOpenPersist(!open)}
    >
      <p className="carryin-sub">{sub}</p>

      <div className="carryin-body">
        {shownSame.length > 0 && (
          <>
            <div className="band">
              <span className="band-h">From past {currentType} projects</span>
              <span className="band-c">{sameType.length}</span>
            </div>
            {shownSame.map(card)}
          </>
        )}
        {shownRest.length > 0 && (
          <>
            <div className="band">
              <span className="band-h">
                {shownSame.length
                  ? 'From similar projects and across the workspace'
                  : 'From across the workspace'}
              </span>
              <span className="band-c">{rest.length}</span>
            </div>
            {shownRest.map(card)}
          </>
        )}
      </div>

      <div className="carryin-foot">
        <button className="link" onClick={() => setView('lessonslibrary')}>
          See all {total} in the Lessons library →
        </button>
        <span className="carryin-note">
          Suggestions only — nothing is added to your project. Reviewed per person.
        </span>
      </div>
    </GuidanceRow>
  );
}
